import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const STATUS_CONFIG = {
  Submitted: { className: 'status-submitted', label: 'Submitted', next: 'In Review', nextLabel: 'Start Review' },
  'In Review': { className: 'status-review', label: 'In Review', next: 'Analyzing', nextLabel: 'Begin Analysis' },
  Analyzing: { className: 'status-analyzing', label: 'Analyzing', next: 'Report Ready', nextLabel: 'Mark Report Ready' },
  'Report Ready': { className: 'status-ready', label: 'Report Ready', next: 'Report Sent', nextLabel: 'Send Report Email' },
  'Report Sent': { className: 'status-sent', label: 'Report Sent', next: 'Completed', nextLabel: 'Mark Completed' },
  Completed: { className: 'status-completed', label: 'Completed', next: null, nextLabel: null },
};

const ALL_STATUSES = ['Submitted', 'In Review', 'Analyzing', 'Report Ready', 'Report Sent', 'Completed'];

export default function AdminPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState('submissions');

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', organization: '', submissionType: '', search: '' });
  const [selectedSub, setSelectedSub] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [adminNotes, setAdminNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  // Coupons state
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    additionalSubmissions: 1,
    expiresAt: '',
    maxRedemptions: '',
  });
  const [couponFormError, setCouponFormError] = useState('');

  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editLimitUser, setEditLimitUser] = useState(null);
  const [newLimitValue, setNewLimitValue] = useState('');

  // Send access code state
  const [sendCodeCoupon, setSendCodeCoupon] = useState(null);
  const [sendCodeUserId, setSendCodeUserId] = useState('');
  const [sendingCode, setSendingCode] = useState(false);

  // Edit coupon state
  const [editCoupon, setEditCoupon] = useState(null);
  const [editCouponForm, setEditCouponForm] = useState({
    code: '',
    additionalSubmissions: 1,
    expiresAt: '',
    maxRedemptions: '',
  });
  const [editCouponError, setEditCouponError] = useState('');
  const [editCouponSaving, setEditCouponSaving] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', messageContent: '', htmlBody: '', isActive: true });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line
  }, []);

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'coupons') {
      if (coupons.length === 0) fetchCoupons();
      if (users.length === 0) fetchUsers(); // needed for "Send to User" dropdown
    }
    if (activeTab === 'users' && users.length === 0) fetchUsers();
    if (activeTab === 'emailTemplates' && emailTemplates.length === 0) fetchEmailTemplates();
    // eslint-disable-next-line
  }, [activeTab]);

  // ============================
  // SUBMISSIONS
  // ============================
  const fetchSubmissions = async (customFilters) => {
    try {
      setLoading(true);
      const f = customFilters || filters;
      const params = new URLSearchParams();
      if (f.status) params.set('status', f.status);
      if (f.organization) params.set('organization', f.organization);
      if (f.submissionType) params.set('submissionType', f.submissionType);
      if (f.search) params.set('search', f.search);

      const res = await fetch(`/api/admin/submissions?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch submissions.');
      const data = await res.json();
      setSubmissions(data.submissions);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchSubmissions(newFilters);
  };

  const clearFilters = () => {
    const cleared = { status: '', organization: '', submissionType: '', search: '' };
    setFilters(cleared);
    fetchSubmissions(cleared);
  };

  const handleStatusChange = async (subId, newStatus) => {
    try {
      setActionLoading(`status-${subId}`);
      const res = await fetch(`/api/admin/submissions/${subId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast(`Status updated to "${newStatus}"`, 'success');
      fetchSubmissions();
      if (selectedSub && selectedSub._id === subId) {
        const singleRes = await fetch(`/api/admin/submissions/${subId}`);
        if (singleRes.ok) {
          const singleData = await singleRes.json();
          setSelectedSub(singleData.submission);
        }
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleUploadReport = async (subId, file) => {
    try {
      setActionLoading(`upload-${subId}`);
      const formData = new FormData();
      formData.append('reportFile', file);

      const res = await fetch(`/api/admin/submissions/${subId}/report`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast('Report uploaded as draft', 'success');
      fetchSubmissions();
      const singleRes = await fetch(`/api/admin/submissions/${subId}`);
      if (singleRes.ok) {
        const singleData = await singleRes.json();
        setSelectedSub(singleData.submission);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handlePublishReport = async (subId) => {
    try {
      setActionLoading(`publish-${subId}`);
      const res = await fetch(`/api/admin/submissions/${subId}/publish-report`, {
        method: 'POST',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast('Report published — now visible to client', 'success');
      fetchSubmissions();
      const singleRes = await fetch(`/api/admin/submissions/${subId}`);
      if (singleRes.ok) {
        const singleData = await singleRes.json();
        setSelectedSub(singleData.submission);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleSendReport = async (subId) => {
    try {
      setActionLoading(`email-${subId}`);
      const res = await fetch(`/api/admin/submissions/${subId}/send-report`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message);
      }
      addToast(data.message, 'success');
      fetchSubmissions();
      const singleRes = await fetch(`/api/admin/submissions/${subId}`);
      if (singleRes.ok) {
        const singleData = await singleRes.json();
        setSelectedSub(singleData.submission);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleRunAnalysis = async (subId) => {
    try {
      setActionLoading(`analyze-${subId}`);
      const res = await fetch(`/api/analysis/${subId}/run`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Analysis failed');
      }
      addToast('Analysis started — results will appear shortly', 'success');
      fetchSubmissions();
      if (selectedSub && selectedSub._id === subId) {
        const singleRes = await fetch(`/api/admin/submissions/${subId}`);
        if (singleRes.ok) {
          const singleData = await singleRes.json();
          setSelectedSub(singleData.submission);
        }
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setActionLoading('');
    }
  };

  const openNotes = async (sub) => {
    setSelectedSub(sub);
    setShowNotes(true);
    setLoadingNotes(true);
    try {
      const res = await fetch(`/api/admin/submissions/${sub._id}/notes`);
      if (!res.ok) throw new Error('Failed to fetch notes.');
      const data = await res.json();
      setAdminNotes(data.adminNotes);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await fetch(`/api/admin/submissions/${selectedSub._id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: noteText }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message);
      }
      setAdminNotes(data.adminNotes);
      setNoteText('');
      addToast('Note added', 'success');
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const openDetail = async (sub) => {
    try {
      const res = await fetch(`/api/admin/submissions/${sub._id}`);
      if (!res.ok) throw new Error('Failed to fetch submission details.');
      const data = await res.json();
      setSelectedSub(data.submission);
      setShowDetail(true);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  // ============================
  // COUPONS
  // ============================
  const fetchCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const res = await fetch('/api/admin/coupons');
      if (!res.ok) throw new Error('Failed to fetch coupons.');
      const data = await res.json();
      setCoupons(data.coupons);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCouponFormError('');

    if (!couponForm.code.trim()) {
      setCouponFormError('Access code is required.');
      return;
    }
    if (couponForm.additionalSubmissions < 1) {
      setCouponFormError('Additional submissions must be at least 1.');
      return;
    }

    try {
      const res = await fetch('/api/admin/coupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponForm.code,
          additionalSubmissions: parseInt(couponForm.additionalSubmissions, 10),
          expiresAt: couponForm.expiresAt || null,
          maxRedemptions: couponForm.maxRedemptions ? parseInt(couponForm.maxRedemptions, 10) : null,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast('Access code created successfully', 'success');
      setCouponForm({ code: '', additionalSubmissions: 1, expiresAt: '', maxRedemptions: '' });
      setShowCreateCoupon(false);
      fetchCoupons();
    } catch (err) {
      setCouponFormError(err.message);
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) return;
    try {
      const res = await fetch(`/api/admin/coupons/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast('Access code deleted', 'success');
      fetchCoupons();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleToggleHold = async (couponId) => {
    try {
      const res = await fetch(`/api/admin/coupons/${couponId}/toggle-hold`, {
        method: 'PATCH',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      fetchCoupons();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleSendCodeToUser = async () => {
    if (!sendCodeCoupon || !sendCodeUserId) {
      addToast('Please select a user.', 'error');
      return;
    }
    setSendingCode(true);
    try {
      const res = await fetch(`/api/admin/coupons/${sendCodeCoupon._id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: sendCodeUserId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      setSendCodeCoupon(null);
      setSendCodeUserId('');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSendingCode(false);
    }
  };

  const openEditCoupon = (coupon) => {
    setEditCoupon(coupon);
    setEditCouponForm({
      code: coupon.code,
      additionalSubmissions: coupon.additionalSubmissions,
      expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().split('T')[0] : '',
      maxRedemptions: coupon.maxRedemptions !== null ? coupon.maxRedemptions : '',
    });
    setEditCouponError('');
  };

  const handleSaveEditCoupon = async (e) => {
    e.preventDefault();
    setEditCouponError('');

    if (!editCouponForm.code.trim()) {
      setEditCouponError('Access code is required.');
      return;
    }
    if (editCouponForm.additionalSubmissions < 1) {
      setEditCouponError('Additional submissions must be at least 1.');
      return;
    }

    setEditCouponSaving(true);
    try {
      const res = await fetch(`/api/admin/coupons/${editCoupon._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: editCouponForm.code,
          additionalSubmissions: parseInt(editCouponForm.additionalSubmissions, 10),
          expiresAt: editCouponForm.expiresAt || null,
          maxRedemptions: editCouponForm.maxRedemptions ? parseInt(editCouponForm.maxRedemptions, 10) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast('Access code updated successfully', 'success');
      setEditCoupon(null);
      fetchCoupons();
    } catch (err) {
      setEditCouponError(err.message);
    } finally {
      setEditCouponSaving(false);
    }
  };

  // ============================
  // USERS
  // ============================
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await fetch('/api/admin/users');
      if (!res.ok) throw new Error('Failed to fetch users.');
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAdjustLimit = async (userId) => {
    const limit = parseInt(newLimitValue, 10);
    if (isNaN(limit) || limit < 0) {
      addToast('Please enter a valid limit (>= 0).', 'error');
      return;
    }
    try {
      const res = await fetch(`/api/admin/users/${userId}/adjust-limit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionLimit: limit }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message);
      }
      addToast('Submission limit updated', 'success');
      setEditLimitUser(null);
      setNewLimitValue('');
      fetchUsers();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleToggleUserActive = async (userId, displayName, currentlyInactive) => {
    const action = currentlyInactive ? 'reactivate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${displayName}?`)) return;
    try {
      const res = await fetch(`/api/admin/users/${userId}/toggle-active`, {
        method: 'PATCH',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
      fetchUsers();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  // ============================
  // EMAIL TEMPLATES
  // ============================
  const fetchEmailTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await fetch('/api/admin/email-templates');
      if (!res.ok) throw new Error('Failed to fetch email templates.');
      const data = await res.json();
      setEmailTemplates(data.templates);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Helper: extract readable text from HTML (for templates that don't have messageContent yet)
  const extractTextFromHtml = (html) => {
    if (!html) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const contentDiv = doc.querySelector('div[style*="padding: 32px"]') || doc.body;
      const paragraphs = [];
      contentDiv.querySelectorAll('p, div[style*="background: #f0f4ff"], a[style*="inline-block"]').forEach(el => {
        // Skip footer elements (after <hr>)
        if (el.previousElementSibling && el.previousElementSibling.tagName === 'HR') return;
        let parent = el.parentElement;
        while (parent && parent !== contentDiv) {
          if (parent.previousElementSibling && parent.previousElementSibling.tagName === 'HR') return;
          parent = parent.parentElement;
        }
        const text = el.textContent.trim();
        if (text && !text.includes('This email was sent by') && !text.includes('contact us at')) {
          paragraphs.push(text);
        }
      });
      return paragraphs.join('\n\n') || html;
    } catch {
      return html;
    }
  };

  const handleEditTemplate = async (template) => {
    setEditingTemplate(template);
    // Use messageContent if available, otherwise extract from htmlBody
    const content = template.messageContent || extractTextFromHtml(template.htmlBody);
    setTemplateForm({
      subject: template.subject,
      messageContent: content,
      htmlBody: template.htmlBody,
      isActive: template.isActive,
    });
    setTemplatePreview(null);
    setTestEmailAddr('');
    setShowHtmlSource(false);

    // Auto-load preview with the messageContent
    try {
      const res = await fetch(`/api/admin/email-templates/${template._id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables: {}, messageContent: content, subject: template.subject }),
      });
      const data = await res.json();
      if (res.ok) {
        setTemplatePreview(data.preview);
      }
    } catch (e) { /* preview auto-load failed, user can click refresh */ }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setTemplateSaving(true);
    try {
      const payload = {
        subject: templateForm.subject,
        messageContent: templateForm.messageContent,
        isActive: templateForm.isActive,
      };
      // Only send htmlBody if advanced HTML was edited directly
      if (showHtmlSource) {
        payload.htmlBody = templateForm.htmlBody;
      }
      const res = await fetch(`/api/admin/email-templates/${editingTemplate._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast('Template saved successfully', 'success');
      setEditingTemplate(null);
      fetchEmailTemplates();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handlePreviewTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const res = await fetch(`/api/admin/email-templates/${editingTemplate._id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: {},
          messageContent: templateForm.messageContent,
          subject: templateForm.subject,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setTemplatePreview(data.preview);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleSendTestEmail = async () => {
    if (!editingTemplate || !testEmailAddr.trim()) return;
    setSendingTest(true);
    try {
      const res = await fetch(`/api/admin/email-templates/${editingTemplate._id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sendTo: testEmailAddr }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      addToast(data.message, 'success');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSendingTest(false);
    }
  };

  // ============================
  // HELPERS
  // ============================
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Safe name helper — handles missing lastName for legacy users
  const safeName = (first, last) => [first, last].filter(Boolean).join(' ') || 'Unknown';

  const getSubmitterName = (sub) => {
    if (sub.userId && typeof sub.userId === 'object') {
      return safeName(sub.userId.fullName, sub.userId.lastName);
    }
    return sub.contactName || 'Unknown';
  };

  const getSubmitterEmail = (sub) => {
    if (sub.userId && typeof sub.userId === 'object') {
      return sub.userId.email;
    }
    return sub.contactEmail || '';
  };

  const getReportStatusBadge = (sub) => {
    if (sub.formalReportRequested && !sub.reportFilePath) {
      return <span className="status-badge status-review">Requested</span>;
    }
    if (!sub.reportFilePath) return null;
    if (sub.reportStatus === 'published') {
      return <span className="status-badge status-completed">Published</span>;
    }
    return <span className="status-badge status-analyzing">Draft</span>;
  };

  // Count by status for summary cards
  const statusCounts = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = submissions.filter((sub) => sub.status === s).length;
    return acc;
  }, {});

  if (loading && submissions.length === 0) {
    return (
      <div className="page-loader">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Admin Panel
          </h1>
          <p className="admin-subtitle">
            Manage migration requests &mdash; logged in as <strong>{user.fullName}</strong>
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'submissions' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Submissions
        </button>
        <button
          className={`admin-tab ${activeTab === 'coupons' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('coupons')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
          Access Codes
        </button>
        <button
          className={`admin-tab ${activeTab === 'users' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          Users
        </button>
        <button
          className={`admin-tab ${activeTab === 'emailTemplates' ? 'admin-tab-active' : ''}`}
          onClick={() => setActiveTab('emailTemplates')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          Email Templates
        </button>
      </div>

      {/* ============================
          TAB: SUBMISSIONS
          ============================ */}
      {activeTab === 'submissions' && (
        <>
          {/* Status Summary Cards */}
          <div className="admin-status-cards">
            {ALL_STATUSES.map((s) => (
              <button
                key={s}
                className={`admin-stat-card ${filters.status === s ? 'admin-stat-card-active' : ''}`}
                onClick={() => handleFilterChange('status', filters.status === s ? '' : s)}
              >
                <span className="admin-stat-count">{statusCounts[s]}</span>
                <span className="admin-stat-label">{s}</span>
              </button>
            ))}
            <button className="admin-stat-card admin-stat-card-total" onClick={clearFilters}>
              <span className="admin-stat-count">{submissions.length}</span>
              <span className="admin-stat-label">Total</span>
            </button>
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="admin-filter-group">
              <input
                type="text"
                placeholder="Search by name, email, org, or app..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="admin-search-input"
              />
            </div>
            <div className="admin-filter-group">
              <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                <option value="">All Statuses</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-filter-group">
              <select value={filters.submissionType} onChange={(e) => handleFilterChange('submissionType', e.target.value)}>
                <option value="">All Types</option>
                <option value="single">Single App</option>
                <option value="mass-import">Mass Import</option>
              </select>
            </div>
            {(filters.status || filters.organization || filters.submissionType || filters.search) && (
              <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>

          {/* Submissions Table */}
          {submissions.length === 0 ? (
            <div className="admin-empty">
              <p>No submissions match your filters.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Application / Portfolio</th>
                    <th>Submitter</th>
                    <th>Organization</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Report</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub) => {
                    const cfg = STATUS_CONFIG[sub.status] || {};
                    return (
                      <tr key={sub._id}>
                        <td className="td-date">{formatShortDate(sub.createdAt)}</td>
                        <td className="td-app-name">
                          <button className="link-button" onClick={() => openDetail(sub)}>
                            {sub.applicationName || sub.portfolioName}
                          </button>
                        </td>
                        <td>
                          <div className="admin-submitter">
                            <span className="admin-submitter-name">{getSubmitterName(sub)}</span>
                            <span className="admin-submitter-email">{getSubmitterEmail(sub)}</span>
                          </div>
                        </td>
                        <td>{sub.organizationName}</td>
                        <td>
                          <span className={`type-badge ${sub.submissionType === 'mass-import' ? 'type-mass' : 'type-single'}`}>
                            {sub.submissionType === 'mass-import' ? 'Portfolio' : 'Single'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${cfg.className || ''}`}>{sub.status}</span>
                        </td>
                        <td>
                          {getReportStatusBadge(sub) || <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <div className="admin-actions-cell">
                            <button className="btn btn-sm btn-outline" onClick={() => openDetail(sub)} title="View details">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            <button className="btn btn-sm btn-outline" onClick={() => openNotes(sub)} title="Admin notes">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </button>
                            {/* Advance status (but skip Report Sent — that goes through email) */}
                            {cfg.next && cfg.next !== 'Report Sent' && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleStatusChange(sub._id, cfg.next)}
                                disabled={actionLoading === `status-${sub._id}`}
                                title={cfg.nextLabel}
                              >
                                {actionLoading === `status-${sub._id}` ? '...' : cfg.nextLabel}
                              </button>
                            )}
                            {/* Run DXL Analysis */}
                            {['Submitted', 'In Review', 'Analyzing'].includes(sub.status) && (!sub.analysisStatus || sub.analysisStatus === 'none' || sub.analysisStatus === 'failed') && (
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={() => handleRunAnalysis(sub._id)}
                                disabled={actionLoading === `analyze-${sub._id}`}
                                title="Run automated DXL analysis"
                              >
                                {actionLoading === `analyze-${sub._id}` ? 'Analyzing...' : 'Analyze'}
                              </button>
                            )}
                            {sub.analysisStatus === 'complete' && (
                              <span className="analysis-badge analysis-complete" title="Analysis complete">Analyzed</span>
                            )}
                            {sub.analysisStatus === 'failed' && (
                              <span className="analysis-badge" style={{ background: 'var(--color-danger)', color: '#fff', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px' }}
                                title={sub.analysisError || 'Analysis failed'}>
                                Failed
                              </span>
                            )}
                            {(sub.analysisStatus === 'pending' || sub.analysisStatus === 'parsing' || sub.analysisStatus === 'analyzing') && (
                              <span className="analysis-badge analysis-running" title="Analysis in progress">Running...</span>
                            )}
                            {/* Upload report */}
                            {['In Review', 'Analyzing', 'Report Ready'].includes(sub.status) && (
                              <>
                                <input
                                  type="file"
                                  accept=".pdf,.zip,.doc,.docx,.xlsx"
                                  style={{ display: 'none' }}
                                  ref={(el) => {
                                    if (el) el.dataset.subId = sub._id;
                                  }}
                                  onChange={(e) => {
                                    if (e.target.files[0]) handleUploadReport(sub._id, e.target.files[0]);
                                  }}
                                />
                                <button
                                  className="btn btn-sm btn-outline"
                                  onClick={(e) => {
                                    const input = e.target.closest('td').querySelector('input[type="file"]');
                                    if (input) input.click();
                                  }}
                                  disabled={actionLoading === `upload-${sub._id}`}
                                  title="Upload report"
                                >
                                  {actionLoading === `upload-${sub._id}` ? '...' : 'Upload'}
                                </button>
                              </>
                            )}
                            {/* Publish draft report */}
                            {sub.reportFilePath && sub.reportStatus === 'draft' && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handlePublishReport(sub._id)}
                                disabled={actionLoading === `publish-${sub._id}`}
                                title="Publish report to client"
                              >
                                {actionLoading === `publish-${sub._id}` ? '...' : 'Publish'}
                              </button>
                            )}
                            {/* Email report (only after published) */}
                            {sub.reportFilePath && sub.reportStatus === 'published' && sub.status === 'Report Ready' && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleSendReport(sub._id)}
                                disabled={actionLoading === `email-${sub._id}`}
                                title="Email report to client"
                              >
                                {actionLoading === `email-${sub._id}` ? 'Sending...' : 'Email'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ============================
          TAB: COUPONS
          ============================ */}
      {activeTab === 'coupons' && (
        <div className="admin-tab-content">
          <div className="admin-tab-header">
            <h2>Access Code Management</h2>
            <button className="btn btn-primary" onClick={() => setShowCreateCoupon(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Create Access Code
            </button>
          </div>

          {loadingCoupons ? (
            <div className="admin-empty"><div className="spinner" /></div>
          ) : coupons.length === 0 ? (
            <div className="admin-empty">
              <p>No access codes created yet. Create one to grant additional submissions.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Grants</th>
                    <th>Redemptions</th>
                    <th>Max Uses</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => {
                    const isHeld = coupon.isHeld;
                    const isExpired = coupon.expiresAt && new Date(coupon.expiresAt) < new Date();
                    const isFullyRedeemed = coupon.maxRedemptions !== null && coupon.redemptions.length >= coupon.maxRedemptions;
                    const isActive = !isHeld && !isExpired && !isFullyRedeemed;

                    return (
                      <tr key={coupon._id} style={isHeld ? { opacity: 0.7 } : undefined}>
                        <td>
                          <code className="coupon-code-display">{coupon.code}</code>
                        </td>
                        <td>+{coupon.additionalSubmissions} submission{coupon.additionalSubmissions > 1 ? 's' : ''}</td>
                        <td>
                          <span className="coupon-redemptions">{coupon.redemptions.length}</span>
                          {coupon.redemptions.length > 0 && (
                            <details className="coupon-redemption-details">
                              <summary className="text-muted">View details</summary>
                              <div className="coupon-redemption-list">
                                {coupon.redemptions.map((r, idx) => (
                                  <div key={idx} className="coupon-redemption-item">
                                    <span>{r.userId ? safeName(r.userId.fullName, r.userId.lastName) : 'Unknown'}</span>
                                    <span className="text-muted">{formatShortDate(r.redeemedAt)}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                        <td>{coupon.maxRedemptions !== null ? coupon.maxRedemptions : '∞'}</td>
                        <td>{coupon.expiresAt ? formatShortDate(coupon.expiresAt) : '—'}</td>
                        <td>
                          {isHeld ? (
                            <span className="status-badge status-hold">On Hold</span>
                          ) : isActive ? (
                            <span className="status-badge status-completed">Active</span>
                          ) : isExpired ? (
                            <span className="status-badge status-analyzing">Expired</span>
                          ) : (
                            <span className="status-badge status-review">Fully Used</span>
                          )}
                        </td>
                        <td className="td-date">{formatShortDate(coupon.createdAt)}</td>
                        <td>
                          <div className="icon-actions">
                            {/* Edit */}
                            <button
                              className="icon-btn icon-btn-default"
                              onClick={() => openEditCoupon(coupon)}
                              title="Edit access code"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            {/* Hold / Resume */}
                            <button
                              className={`icon-btn ${isHeld ? 'icon-btn-success' : 'icon-btn-warning'}`}
                              onClick={() => handleToggleHold(coupon._id)}
                              title={isHeld ? 'Resume this access code' : 'Put this access code on hold'}
                            >
                              {isHeld ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                              ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                              )}
                            </button>
                            {/* Send to user */}
                            <button
                              className="icon-btn icon-btn-primary"
                              onClick={() => { setSendCodeCoupon(coupon); setSendCodeUserId(''); }}
                              title="Send access code to a user via email"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                            </button>
                            {/* Delete */}
                            <button
                              className="icon-btn icon-btn-danger"
                              onClick={() => handleDeleteCoupon(coupon._id)}
                              title="Delete access code"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Send Code to User Dialog */}
          {sendCodeCoupon && (
            <div className="dialog-overlay" onClick={() => setSendCodeCoupon(null)}>
              <div className="dialog dialog-md" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-header">
                  <h3 className="dialog-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Send Access Code to User
                  </h3>
                  <button className="dialog-close-btn" onClick={() => setSendCodeCoupon(null)}>&times;</button>
                </div>
                <div className="dialog-body">
                  <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--color-bg-secondary, #f8fafc)', borderRadius: 8, border: '1px solid var(--color-border, #e2e8f0)' }}>
                    <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>Access Code</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.08em', color: 'var(--color-primary)' }}>{sendCodeCoupon.code}</div>
                    <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 4 }}>+{sendCodeCoupon.additionalSubmissions} submission{sendCodeCoupon.additionalSubmissions > 1 ? 's' : ''}</div>
                  </div>
                  <div className="form-group">
                    <label>Select User</label>
                    <select
                      value={sendCodeUserId}
                      onChange={(e) => setSendCodeUserId(e.target.value)}
                      style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border, #e2e8f0)', fontSize: '0.9rem' }}
                    >
                      <option value="">— Choose a user —</option>
                      {users.map((u) => (
                        <option key={u._id} value={u._id}>
                          {safeName(u.fullName, u.lastName)} — {u.email} ({u.organizationName || 'No org'})
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="text-muted" style={{ fontSize: '0.82rem', marginTop: 8 }}>
                    An email with the access code will be sent to the selected user.
                  </p>
                </div>
                <div className="dialog-actions">
                  <button className="btn btn-ghost" onClick={() => setSendCodeCoupon(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSendCodeToUser} disabled={sendingCode || !sendCodeUserId}>
                    {sendingCode ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Coupon Dialog */}
          {editCoupon && (
            <div className="dialog-overlay" onClick={() => setEditCoupon(null)}>
              <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-header">
                  <h3 className="dialog-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit Access Code
                  </h3>
                  <button className="dialog-close-btn" onClick={() => setEditCoupon(null)}>&times;</button>
                </div>
                <form onSubmit={handleSaveEditCoupon}>
                  <div className="dialog-body">
                    {editCouponError && (
                      <div className="coupon-form-error">{editCouponError}</div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                      <label>Access Code *</label>
                      <input
                        type="text"
                        value={editCouponForm.code}
                        onChange={(e) => setEditCouponForm({ ...editCouponForm, code: e.target.value.toUpperCase() })}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '0.05em' }}
                      />
                    </div>
                    <div className="form-row" style={{ marginBottom: '0.85rem' }}>
                      <div className="form-group">
                        <label>Additional Submissions *</label>
                        <input
                          type="number"
                          min="1"
                          value={editCouponForm.additionalSubmissions}
                          onChange={(e) => setEditCouponForm({ ...editCouponForm, additionalSubmissions: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Max Redemptions</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="Unlimited"
                          value={editCouponForm.maxRedemptions}
                          onChange={(e) => setEditCouponForm({ ...editCouponForm, maxRedemptions: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Expiry Date</label>
                      <input
                        type="date"
                        value={editCouponForm.expiresAt}
                        onChange={(e) => setEditCouponForm({ ...editCouponForm, expiresAt: e.target.value })}
                      />
                      <span className="inline-muted-hint">Leave empty for no expiry.</span>
                    </div>
                    {editCoupon.redemptions && editCoupon.redemptions.length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'var(--color-bg-secondary, #f8fafc)', borderRadius: 8, border: '1px solid var(--color-border, #e2e8f0)' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                          Redemptions ({editCoupon.redemptions.length})
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {editCoupon.redemptions.map((r, idx) => (
                            <div key={idx} style={{ marginBottom: 4 }}>
                              {r.userId ? safeName(r.userId.fullName, r.userId.lastName) : 'Unknown'} — {formatShortDate(r.redeemedAt)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="dialog-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => setEditCoupon(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={editCouponSaving}>
                      {editCouponSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Create Coupon Dialog */}
          {showCreateCoupon && (
            <div className="dialog-overlay" onClick={() => setShowCreateCoupon(false)}>
              <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-header">
                  <h3 className="dialog-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                    Create New Access Code
                  </h3>
                  <button className="dialog-close-btn" onClick={() => setShowCreateCoupon(false)}>&times;</button>
                </div>
                <form onSubmit={handleCreateCoupon}>
                  <div className="dialog-body">
                    {couponFormError && (
                      <div className="coupon-form-error">{couponFormError}</div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                      <label>Access Code *</label>
                      <input
                        type="text"
                        placeholder="e.g. RIVIT2026"
                        value={couponForm.code}
                        onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '0.05em' }}
                      />
                    </div>
                    <div className="form-row" style={{ marginBottom: '0.85rem' }}>
                      <div className="form-group">
                        <label>Additional Submissions *</label>
                        <input
                          type="number"
                          min="1"
                          value={couponForm.additionalSubmissions}
                          onChange={(e) => setCouponForm({ ...couponForm, additionalSubmissions: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Max Redemptions</label>
                        <input
                          type="number"
                          min="1"
                          placeholder="Unlimited"
                          value={couponForm.maxRedemptions}
                          onChange={(e) => setCouponForm({ ...couponForm, maxRedemptions: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Expiry Date</label>
                      <input
                        type="date"
                        value={couponForm.expiresAt}
                        onChange={(e) => setCouponForm({ ...couponForm, expiresAt: e.target.value })}
                      />
                      <span className="inline-muted-hint">Leave empty for no expiry.</span>
                    </div>
                  </div>
                  <div className="dialog-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => setShowCreateCoupon(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create Access Code</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================
          TAB: USERS
          ============================ */}
      {activeTab === 'users' && (
        <div className="admin-tab-content">
          <div className="admin-tab-header">
            <h2>User Management</h2>
            <button className="btn btn-outline" onClick={fetchUsers}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Refresh
            </button>
          </div>

          {loadingUsers ? (
            <div className="admin-empty"><div className="spinner" /></div>
          ) : users.length === 0 ? (
            <div className="admin-empty">
              <p>No users found.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Role</th>
                    <th>Used / Limit</th>
                    <th>Last Login</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isInactive = u.isActive === false;
                    return (
                    <tr key={u._id} className={isInactive ? 'user-row-inactive' : ''}>
                      <td className="td-app-name">
                        {safeName(u.fullName, u.lastName)}
                        {isInactive && (
                          <span className="user-inactive-badge">Inactive</span>
                        )}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.organizationName}</td>
                      <td>
                        <span className={`type-badge ${u.role === 'admin' ? 'type-mass' : 'type-single'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <span className="user-quota-display">
                          <strong>{u.submissionsUsed || 0}</strong> / {u.submissionLimit || 1}
                        </span>
                      </td>
                      <td className="td-date">
                        {u.lastLoginAt ? (
                          <div className="user-last-login-cell">
                            <div>{formatShortDate(u.lastLoginAt)}</div>
                            {u.lastLoginLocation && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '2px' }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                {u.lastLoginLocation}
                              </div>
                            )}
                            {u.lastLoginIp && (
                              <div className="text-muted" style={{ fontSize: '0.7rem', opacity: 0.7 }}>IP: {u.lastLoginIp}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Never</span>
                        )}
                      </td>
                      <td className="td-date">
                        {formatShortDate(u.createdAt)}
                        {isInactive && u.deactivatedAt && (
                          <div className="user-deactivated-info">
                            <span className="text-muted" style={{ fontSize: '0.72rem', color: 'var(--color-danger)' }}>
                              Deactivated {formatShortDate(u.deactivatedAt)}
                            </span>
                            {u.deactivatedBy && typeof u.deactivatedBy === 'object' && (
                              <span className="text-muted" style={{ fontSize: '0.7rem', color: 'var(--color-danger)' }}>
                                by {u.deactivatedBy._id === u._id ? 'self' : safeName(u.deactivatedBy.fullName, u.deactivatedBy.lastName)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="icon-actions">
                          {editLimitUser === u._id ? (
                            <div className="admin-inline-edit">
                              <input
                                type="number"
                                min="0"
                                value={newLimitValue}
                                onChange={(e) => setNewLimitValue(e.target.value)}
                                className="admin-inline-input"
                                placeholder="New limit"
                                autoFocus
                              />
                              <button className="btn btn-sm btn-primary" onClick={() => handleAdjustLimit(u._id)}>Save</button>
                              <button className="btn btn-sm btn-ghost" onClick={() => { setEditLimitUser(null); setNewLimitValue(''); }}>Cancel</button>
                            </div>
                          ) : (
                            <>
                              <button
                                className="icon-btn icon-btn-default"
                                onClick={() => { setEditLimitUser(u._id); setNewLimitValue(String(u.submissionLimit || 1)); }}
                                title="Adjust submission limit"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              </button>
                              {u.role !== 'admin' && (
                                <button
                                  className={`icon-btn ${isInactive ? 'icon-btn-success' : 'icon-btn-danger'}`}
                                  onClick={() => handleToggleUserActive(u._id, safeName(u.fullName, u.lastName), isInactive)}
                                  title={isInactive ? 'Reactivate user' : 'Deactivate user'}
                                >
                                  {isInactive ? (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                  ) : (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                  )}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================
          TAB: EMAIL TEMPLATES
          ============================ */}
      {activeTab === 'emailTemplates' && (
        <div className="admin-tab-content">
          <div className="admin-tab-header">
            <h2>Email Templates</h2>
            <button className="btn btn-outline" onClick={fetchEmailTemplates}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Refresh
            </button>
          </div>

          {loadingTemplates ? (
            <div className="admin-empty"><div className="spinner" /></div>
          ) : emailTemplates.length === 0 ? (
            <div className="admin-empty">
              <p>No email templates found. Restart the server to seed defaults.</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Key</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emailTemplates.map((tmpl) => (
                    <tr key={tmpl._id}>
                      <td>
                        <strong>{tmpl.name}</strong>
                        <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>{tmpl.description}</div>
                      </td>
                      <td><code className="coupon-code-display">{tmpl.key}</code></td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tmpl.subject}</td>
                      <td>
                        {tmpl.isActive ? (
                          <span className="status-badge status-completed">Active</span>
                        ) : (
                          <span className="status-badge status-analyzing">Disabled</span>
                        )}
                      </td>
                      <td className="td-date">
                        {tmpl.updatedAt ? formatShortDate(tmpl.updatedAt) : '—'}
                        {tmpl.updatedBy && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            by {tmpl.updatedBy.fullName || tmpl.updatedBy.email}
                          </div>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-primary" onClick={() => handleEditTemplate(tmpl)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Template Editor Dialog */}
          {editingTemplate && (
            <div className="dialog-overlay" onClick={() => setEditingTemplate(null)}>
              <div className="dialog dialog-xl" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960 }}>
                <div className="dialog-header">
                  <h3 className="dialog-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Edit: {editingTemplate.name}
                  </h3>
                  <button className="dialog-close-btn" onClick={() => setEditingTemplate(null)}>&times;</button>
                </div>
                <div className="dialog-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                  {/* Template meta bar */}
                  <div className="template-info-bar">
                    <div className="template-info-row">
                      <span className="template-info-label">Template</span>
                      <code className="template-info-code">{editingTemplate.key}</code>
                      <span className="text-muted" style={{ marginLeft: 8, fontSize: '0.8rem' }}>{editingTemplate.description}</span>
                    </div>
                    <div className="template-info-row">
                      <span className="template-info-label">Variables</span>
                      <div className="template-var-list">
                        {editingTemplate.variables.map((v) => (
                          <code key={v} className="template-var-tag" title={`Click to copy {{${v}}}`} style={{ cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(`{{${v}}}`); addToast(`Copied {{${v}}}`, 'info', 1500); }}>{'{{' + v + '}}'}</code>
                        ))}
                      </div>
                    </div>
                    <div className="template-info-row">
                      <label className="template-active-toggle">
                        <input
                          type="checkbox"
                          checked={templateForm.isActive}
                          onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })}
                        />
                        <span>{templateForm.isActive ? 'Active — emails will be sent' : 'Disabled — emails will NOT be sent'}</span>
                      </label>
                    </div>
                  </div>

                  {/* Subject Line */}
                  <div className="form-group" style={{ marginTop: '1.25rem' }}>
                    <label className="template-field-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 4 }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                      placeholder="Email subject — use {{variableName}} for dynamic content"
                      className="template-modern-input"
                    />
                  </div>

                  {/* Message Content — the primary editing area */}
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label className="template-field-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 4 }}><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
                      Email Message
                    </label>
                    <textarea
                      value={templateForm.messageContent}
                      onChange={(e) => setTemplateForm({ ...templateForm, messageContent: e.target.value })}
                      rows={10}
                      className="template-message-editor"
                      placeholder={'Write your email message here.\n\nUse {{variableName}} for dynamic content.\nBlank lines create new paragraphs.\n**bold text** for emphasis.\n[link text]({{url}}) for links.\n> quoted text for highlights.'}
                    />
                    <div className="template-format-hints">
                      <span title="Blank line = new paragraph">¶ Paragraphs</span>
                      <span title="**bold text**">
                        <strong>B</strong> Bold
                      </span>
                      <span title="[text](url)">[🔗] Link</span>
                      <span title="> quoted text">❝ Quote</span>
                      <span title="[button:Text](url)">▶ Button</span>
                      <span title="[code]VALUE[/code]">⌨ Code Box</span>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="template-preview-container" style={{ marginTop: '1rem' }}>
                    <div className="template-preview-header">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      <span>Preview</span>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: '0.75rem' }}
                        onClick={handlePreviewTemplate}
                        type="button"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                        Refresh Preview
                      </button>
                    </div>
                    {templatePreview ? (
                      <>
                        <div className="template-preview-subject">
                          <strong>Subject:</strong> {templatePreview.subject}
                        </div>
                        <div
                          className="template-preview-body"
                          dangerouslySetInnerHTML={{ __html: templatePreview.htmlBody }}
                        />
                      </>
                    ) : (
                      <div className="template-preview-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-muted)' }}>
                        <div className="spinner" style={{ margin: '0 auto 0.5rem' }} />
                        Loading preview...
                      </div>
                    )}
                  </div>

                  {/* Collapsible Advanced HTML Source */}
                  <div className="template-html-collapsible" style={{ marginTop: '1rem' }}>
                    <button
                      className="template-html-toggle"
                      onClick={() => setShowHtmlSource(!showHtmlSource)}
                      type="button"
                    >
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transition: 'transform 0.2s', transform: showHtmlSource ? 'rotate(90deg)' : 'rotate(0deg)' }}
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
                      </svg>
                      <span>Advanced: Raw HTML Source</span>
                    </button>
                    {showHtmlSource && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <textarea
                          value={templateForm.htmlBody}
                          onChange={(e) => setTemplateForm({ ...templateForm, htmlBody: e.target.value })}
                          rows={14}
                          className="template-html-editor"
                          placeholder="Raw HTML email body"
                        />
                        <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: '0.3rem' }}>
                          Direct HTML overrides message content above when saved. Use with caution.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Test Email — modern card */}
                  <div className="template-test-card">
                    <div className="template-test-card-header">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                      <span>Send Test Email</span>
                    </div>
                    <div className="template-test-card-body">
                      <div className="template-test-input-group">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="template-test-input-icon"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        <input
                          type="email"
                          placeholder="Enter recipient email address"
                          value={testEmailAddr}
                          onChange={(e) => setTestEmailAddr(e.target.value)}
                          className="template-test-input"
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSendTestEmail}
                          disabled={sendingTest || !testEmailAddr.trim()}
                          type="button"
                        >
                          {sendingTest ? (
                            <><div className="spinner spinner-sm" style={{ marginRight: 4 }} /> Sending...</>
                          ) : (
                            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>Send</>
                          )}
                        </button>
                      </div>
                      <p className="text-muted" style={{ fontSize: '0.74rem', margin: '0.4rem 0 0' }}>
                        Sends a test email with sample data to verify formatting.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="dialog-actions">
                  <button className="btn btn-ghost" onClick={() => setEditingTemplate(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={templateSaving}>
                    {templateSaving ? (
                      <><div className="spinner spinner-sm" style={{ marginRight: 6 }} /> Saving...</>
                    ) : (
                      <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>Save Template</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================
          Detail Dialog
          ============================ */}
      {showDetail && selectedSub && (
        <div className="dialog-overlay" onClick={() => setShowDetail(false)}>
          <div className="dialog dialog-xl" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                {selectedSub.applicationName || selectedSub.portfolioName}
              </h3>
              <button className="dialog-close-btn" onClick={() => setShowDetail(false)}>
                &times;
              </button>
            </div>
            <div className="dialog-body">
              {/* Status bar with workflow */}
              <div className="admin-detail-status-bar">
                {ALL_STATUSES.map((s, idx) => {
                  const currentIdx = ALL_STATUSES.indexOf(selectedSub.status);
                  const isPast = idx < currentIdx;
                  const isCurrent = idx === currentIdx;
                  return (
                    <div key={s} className={`admin-status-step ${isPast ? 'step-past' : ''} ${isCurrent ? 'step-current' : ''}`}>
                      <div className="admin-status-step-dot" />
                      <span className="admin-status-step-label">{s}</span>
                    </div>
                  );
                })}
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Submitter</span>
                  <span className="detail-value">{getSubmitterName(selectedSub)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{getSubmitterEmail(selectedSub)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Organization</span>
                  <span className="detail-value">{selectedSub.organizationName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Submission Type</span>
                  <span className="detail-value">
                    <span className={`type-badge ${selectedSub.submissionType === 'mass-import' ? 'type-mass' : 'type-single'}`}>
                      {selectedSub.submissionType === 'mass-import' ? 'Portfolio' : 'Single App'}
                    </span>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Target Platform</span>
                  <span className="detail-value">{selectedSub.targetPlatform}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Priority</span>
                  <span className="detail-value">
                    <span className={`priority-badge priority-${selectedSub.priority?.toLowerCase()}`}>{selectedSub.priority}</span>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">
                    <span className={`status-badge ${(STATUS_CONFIG[selectedSub.status] || {}).className}`}>{selectedSub.status}</span>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Submitted</span>
                  <span className="detail-value">{formatDate(selectedSub.createdAt)}</span>
                </div>
                {selectedSub.submissionType !== 'mass-import' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">Approx. Users</span>
                      <span className="detail-value">{selectedSub.approximateUsers || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Approx. Documents</span>
                      <span className="detail-value">{selectedSub.approximateDocuments || 0}</span>
                    </div>
                  </>
                )}
                {selectedSub.submissionType === 'mass-import' && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">Total Applications</span>
                      <span className="detail-value">{selectedSub.totalApplications}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Shared Database</span>
                      <span className="detail-value">{selectedSub.sharedDatabase || 'N/A'}</span>
                    </div>
                  </>
                )}
                <div className="detail-item">
                  <span className="detail-label">Domino Version</span>
                  <span className="detail-value">{selectedSub.dominoServerVersion || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Migrate Data</span>
                  <span className="detail-value">{selectedSub.migrateDatabaseData || 'N/A'}</span>
                </div>
              </div>

              {selectedSub.applicationDescription && (
                <div className="detail-section">
                  <h4>Description</h4>
                  <p>{selectedSub.applicationDescription}</p>
                </div>
              )}

              {selectedSub.additionalNotes && (
                <div className="detail-section">
                  <h4>Client Notes</h4>
                  <p>{selectedSub.additionalNotes}</p>
                </div>
              )}

              {selectedSub.complianceDetails && (
                <div className="detail-section">
                  <h4>Compliance Details</h4>
                  <p>{selectedSub.complianceDetails}</p>
                </div>
              )}

              {selectedSub.integrationDetails && (
                <div className="detail-section">
                  <h4>Integration Details</h4>
                  <p>{selectedSub.integrationDetails}</p>
                </div>
              )}

              {/* DXL Analysis Section */}
              <div className="detail-section">
                <h4>Automated Analysis</h4>
                {selectedSub.analysisStatus === 'complete' && selectedSub.analysisSummary ? (
                  <div className="analysis-summary-grid">
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.totalForms || 0}</span>
                      <span className="analysis-stat-label">Forms</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.totalFields || 0}</span>
                      <span className="analysis-stat-label">Fields</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.totalViews || 0}</span>
                      <span className="analysis-stat-label">Views</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.totalAgents || 0}</span>
                      <span className="analysis-stat-label">Agents</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.complexityLabel || 'N/A'}</span>
                      <span className="analysis-stat-label">Complexity</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.estimatedEffortWeeks || 0}w</span>
                      <span className="analysis-stat-label">Est. Effort</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.detectedDomain || 'Unknown'}</span>
                      <span className="analysis-stat-label">Domain</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.roleCount || 0}</span>
                      <span className="analysis-stat-label">Roles</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.migrationRiskLevel || 'N/A'}</span>
                      <span className="analysis-stat-label">Risk Level</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.migrationPhaseCount || 0}</span>
                      <span className="analysis-stat-label">Phases</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.duplicateForms || 0}</span>
                      <span className="analysis-stat-label">Duplicates</span>
                    </div>
                    <div className="analysis-stat">
                      <span className="analysis-stat-value">{selectedSub.analysisSummary.manyToManyCandidates || 0}</span>
                      <span className="analysis-stat-label">M2M Relations</span>
                    </div>
                    {selectedSub.analysisResults && selectedSub.analysisResults.migration && selectedSub.analysisResults.migration.resourceEstimate && (
                      <>
                        <div className="analysis-stat">
                          <span className="analysis-stat-value">{selectedSub.analysisResults.migration.resourceEstimate.teamSize}</span>
                          <span className="analysis-stat-label">Team Size</span>
                        </div>
                        <div className="analysis-stat">
                          <span className="analysis-stat-value">{selectedSub.analysisResults.migration.resourceEstimate.parallelMonths}mo</span>
                          <span className="analysis-stat-label">Timeline</span>
                        </div>
                      </>
                    )}
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                      Analyzed in {selectedSub.analysisDuration || 0}ms on {selectedSub.analysisCompletedAt ? formatDate(selectedSub.analysisCompletedAt) : 'N/A'}
                    </div>
                  </div>
                ) : selectedSub.analysisStatus === 'failed' ? (
                  <div>
                    <span className="text-muted">Analysis failed: {selectedSub.analysisError || 'Unknown error'}</span>
                    <button className="btn btn-sm btn-outline" style={{ marginLeft: '0.5rem' }}
                      onClick={() => handleRunAnalysis(selectedSub._id)}
                      disabled={actionLoading === `analyze-${selectedSub._id}`}
                    >
                      Retry
                    </button>
                  </div>
                ) : (selectedSub.analysisStatus === 'pending' || selectedSub.analysisStatus === 'parsing' || selectedSub.analysisStatus === 'analyzing') ? (
                  <span className="analysis-badge analysis-running">Analysis in progress...</span>
                ) : (
                  <div>
                    <span className="text-muted">No analysis yet. </span>
                    <button className="btn btn-sm btn-primary"
                      onClick={() => handleRunAnalysis(selectedSub._id)}
                      disabled={actionLoading === `analyze-${selectedSub._id}`}
                    >
                      {actionLoading === `analyze-${selectedSub._id}` ? 'Starting...' : 'Run Analysis'}
                    </button>
                  </div>
                )}
              </div>

              {/* Report Section */}
              <div className="detail-section">
                <h4>Report</h4>
                {selectedSub.formalReportRequested && (
                  <div className="admin-report-request-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>
                      <strong>Formal report requested</strong> by client on {formatDate(selectedSub.formalReportRequestedAt)}
                    </span>
                  </div>
                )}
                <div className="admin-report-section">
                  {!selectedSub.reportFilePath ? (
                    <span className="text-muted">No report uploaded yet.</span>
                  ) : (
                    <div className="admin-report-info">
                      <div className="admin-report-info-row">
                        <span>Report Status:</span>
                        {selectedSub.reportStatus === 'published' ? (
                          <span className="status-badge status-completed">Published</span>
                        ) : (
                          <span className="status-badge status-analyzing">Draft</span>
                        )}
                      </div>
                      {selectedSub.reportUploadedAt && (
                        <div className="admin-report-info-row">
                          <span>Uploaded:</span>
                          <span>{formatDate(selectedSub.reportUploadedAt)}</span>
                        </div>
                      )}
                      {selectedSub.reportPublishedAt && (
                        <div className="admin-report-info-row">
                          <span>Published:</span>
                          <span>{formatDate(selectedSub.reportPublishedAt)}</span>
                        </div>
                      )}
                      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem' }}>
                        <a
                          href={`/api/admin/submissions/${selectedSub._id}/report-download`}
                          className="btn btn-outline btn-sm"
                          style={{ display: 'inline-flex' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download Report
                        </a>
                        {selectedSub.reportStatus === 'draft' && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handlePublishReport(selectedSub._id)}
                            disabled={actionLoading === `publish-${selectedSub._id}`}
                          >
                            {actionLoading === `publish-${selectedSub._id}` ? 'Publishing...' : 'Publish to Client'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status History */}
              {selectedSub.statusHistory && selectedSub.statusHistory.length > 0 && (
                <div className="detail-section">
                  <h4>Status History</h4>
                  <div className="admin-status-history">
                    {selectedSub.statusHistory.map((entry, idx) => (
                      <div key={idx} className="admin-history-entry">
                        <span className="admin-history-status">
                          <span className={`status-badge ${(STATUS_CONFIG[entry.status] || {}).className}`}>{entry.status}</span>
                        </span>
                        <span className="admin-history-note">{entry.note}</span>
                        <span className="admin-history-meta">
                          {entry.changedBy && typeof entry.changedBy === 'object'
                            ? safeName(entry.changedBy.fullName, entry.changedBy.lastName)
                            : 'System'}{' '}
                          — {formatDate(entry.changedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File download */}
              <div className="detail-section">
                <h4>Uploaded File</h4>
                <a
                  href={`/api/admin/submissions/${selectedSub._id}/download`}
                  className="btn btn-outline btn-sm"
                  style={{ display: 'inline-flex' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download DXL File
                </a>
              </div>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowDetail(false)}>
                Close
              </button>
              <button className="btn btn-outline" onClick={() => { setShowDetail(false); openNotes(selectedSub); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Notes
              </button>
              {STATUS_CONFIG[selectedSub.status]?.next && STATUS_CONFIG[selectedSub.status].next !== 'Report Sent' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleStatusChange(selectedSub._id, STATUS_CONFIG[selectedSub.status].next);
                    setShowDetail(false);
                  }}
                >
                  {STATUS_CONFIG[selectedSub.status].nextLabel}
                </button>
              )}
              {selectedSub.reportFilePath && selectedSub.reportStatus === 'published' && selectedSub.status === 'Report Ready' && (
                <button
                  className="btn btn-success"
                  onClick={() => {
                    handleSendReport(selectedSub._id);
                    setShowDetail(false);
                  }}
                >
                  Email Report to Client
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notes Dialog */}
      {showNotes && selectedSub && (
        <div className="dialog-overlay" onClick={() => setShowNotes(false)}>
          <div className="dialog dialog-lg" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3 className="dialog-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Internal Notes — {selectedSub.applicationName || selectedSub.portfolioName}
              </h3>
              <button className="dialog-close-btn" onClick={() => setShowNotes(false)}>
                &times;
              </button>
            </div>
            <div className="dialog-body">
              <p className="admin-notes-hint">These notes are only visible to admins — not shared with the client.</p>

              {loadingNotes ? (
                <div className="admin-notes-loading">
                  <div className="spinner" />
                </div>
              ) : adminNotes.length === 0 ? (
                <p className="text-muted" style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                  No notes yet. Add the first one below.
                </p>
              ) : (
                <div className="admin-notes-list">
                  {adminNotes.map((note, idx) => (
                    <div key={idx} className="admin-note-item">
                      <div className="admin-note-header">
                        <span className="admin-note-author">
                          {note.createdBy && typeof note.createdBy === 'object'
                            ? safeName(note.createdBy.fullName, note.createdBy.lastName)
                            : 'Admin'}
                        </span>
                        <span className="admin-note-date">{formatDate(note.createdAt)}</span>
                      </div>
                      <p className="admin-note-text">{note.text}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-note-form">
                <textarea
                  placeholder="Add an internal note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                />
                <button className="btn btn-primary btn-sm" onClick={handleAddNote} disabled={!noteText.trim()}>
                  Add Note
                </button>
              </div>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowNotes(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
